import { TranslationsContext } from '@teliaee/telia-translate';
import { observer } from 'mobx-react';
import { BanklinkV2PaymentReturn, CardPaymentReturn, Payment } from '@teliaee/payment-react-component';
import * as React from 'react';
import { Button, Grid, GridColumn, Separator } from 'telia-front-react';

import config from '../config';
import { useStores } from '../stores/RootStore';
import { PrepaymentSteps } from './../Constants';

interface IPaymentMethodProps {
  disabled: boolean;
  setStepIndex: (index: PrepaymentSteps) => void;
}

const PaymentMethods = observer((props: IPaymentMethodProps) => {
  const { disabled, setStepIndex } = props;
  const { paymentMethodsStore, posPaymentStore, sessionStore, priceStore } = useStores();
  const translate = React.useContext(TranslationsContext).translate;

  const handleBackButtonClick = () => {
    setStepIndex(PrepaymentSteps.PRICE_STEP);
  };

  const renderBackButton = (): JSX.Element => {
    return (
      <Grid align={['between-xs', 'middle-xs']}>
        <GridColumn width={['xs']} className="text-right">
          <Button size="small" link disabled={disabled} iconLeft="#arrow-left" text={translate('prepaid.steps.previous')} onClick={handleBackButtonClick} />
        </GridColumn>
      </Grid>
    );
  };

  const renderPaymentMethods = (): JSX.Element => {
    return <>{sessionStore.isDealer ? renderDealerPaymentMethod() : renderClientPaymentMethods()}</>;
  };

  const renderClientPaymentMethods = (): JSX.Element => {
    const store = paymentMethodsStore;
    return (
      <Payment
        referenceNumber={store.referenceNumber}
        environment={config.frontendEnvironmentName}
        source={store.paymentSource}
        language={store.language}
        initialAmount={store.paymentAmount}
        brand={store.brand}
        paymentMethods={store.paymentMethods}
        banklinkPayment={store.bankLinkPaymentProps}
        cardPayment={{ redirectFrameTarget: '_self', returnUrl: paymentMethodsStore.returnUrl || window.location.href, shopperReference: null }}
        headless={true}
        paymentOrderParams={store.paymentOrderParams}
      />
    );
  };

  const renderDealerPaymentMethod = (): JSX.Element => {
    return (
      <Grid className="grid--prepaid" align={['middle-xs']}>
        <GridColumn width={['md-9', 'lg-7', 'xl-6']}>
          <Button type="employee" text={translate('prepaid.step.pos-payment-button')} onClick={posPaymentStore.createPayment} />
        </GridColumn>
      </Grid>
    );
  };

  const renderPaymentComponent = () => {
    if (paymentMethodsStore.banklinkTransactionReference) {
      return (
        <BanklinkV2PaymentReturn
          banklinkTransactionReference={paymentMethodsStore.banklinkTransactionReference}
          finalizePaymentProps={{
            onFinalStatus: paymentMethodsStore.goToPaymentStatus,
          }}
          environment={config.frontendEnvironmentName}
          brand={paymentMethodsStore.brand}
          language={paymentMethodsStore.language}
        />
      );
    }

    if (paymentMethodsStore.cardTransactionReference) {
      return (
        <CardPaymentReturn
          cardTransactionReference={paymentMethodsStore.cardTransactionReference}
          finalizePaymentProps={{
            onFinalStatus: paymentMethodsStore.goToPaymentStatus,
          }}
          environment={config.frontendEnvironmentName}
          brand={paymentMethodsStore.brand}
          language={paymentMethodsStore.language}
        />
      );
    }

    if (!disabled) {
      return renderPaymentMethods();
    }
  };

  if (!priceStore.canProceedToPayment) {
    return null;
  }

  return (
    <>
      {renderPaymentComponent()}
      <Separator spacing="xsmall" color="invisible" />
      {renderBackButton()}
    </>
  );
});

export default PaymentMethods;
